import { useEffect } from 'react';

import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import {
	type LoaderFunctionArgs,
	redirect,
	useNavigate,
	useSearchParams
} from 'react-router-dom';
import { toast } from 'react-toastify';

import { env } from '@/lib/env';
import { authMutations } from '@/queries/auth/auth.queries';
import { useAuthStore } from '@/store';
import { Roles } from '@/types/roles';

export function CallbackLoader({ request }: LoaderFunctionArgs) {
	const token = useAuthStore.getState().access_token;
	const url = new URL(request.url);

	const code = url.searchParams.get('code');

	if (!token && !code) {
		return redirect('/login');
	}

	return true;
}

export default function Callback() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const accessToken = useAuthStore((s) => s.access_token);
	const onLogin = useAuthStore((s) => s.onLogin);

	const code = searchParams.get('code');

	const login = useMutation(authMutations.login);

	useEffect(() => {
		if (code && !accessToken) {
			login.mutate(
				{
					code,
					redirect_uri: env.VITE_DUENDE_REDIRECT_URI,
					onLogin
				},
				{
					onSuccess: ({ data: user }) => {
						Sentry.setUser({
							id: user.id,
							email: user.email,
							role: user.roleId,
							name: user.email,
							vendorName: user.vendorName,
							vendorEmail: user.vendorEmail,
							businessAreaId: user.businessAreaId,
							businessAreaName: user.businessAreaName,
							companyId: user.companyId,
							companyName: user.companyName,
							phoneNumber: user.phoneNumber
						});

						if (
							[
								Roles.AccountManager,
								Roles.Procurement,
								Roles.Compliance,
								Roles.VendorAuthorizedOfficer
							].includes(user.roleId as string)
						)
							return navigate('/home', { replace: true, viewTransition: true });

						setTimeout(() => {
							globalThis.location.href = '/cms/';
						}, 200);
					},
					onError: (error) => {
						toast.error(error.message);
						navigate('/login', { replace: true, viewTransition: true });
					}
				}
			);
		}
	}, [code]);

	useEffect(() => {
		if (!accessToken && !code) {
			navigate('/login', { replace: true, viewTransition: true });
		}
	}, [accessToken]);

	return (
		<div className="mx-auto flex h-screen w-screen flex-col items-center justify-center overflow-hidden">
			<div className="loader mx-auto my-3 h-10 w-10 rounded-full border-4 border-t-4 border-gray-200 text-center ease-linear"></div>
			<h2 className="text-md text-center font-semibold text-black">
				Logging in...
			</h2>
		</div>
	);
}
